import type { InkStroke } from '../model/schema.js';
import { newId } from '../model/ids.js';
import type { ElementLookup } from './attach.js';
import { strokeBoardPoints, withBoardPoints } from './attach.js';
import type { Affine, RotatedRect } from './geometry.js';
import { applyAffine, boundsOf, pointInPolygon, rectCenter, scaleFactor } from './geometry.js';
import type { Pt } from './types.js';

/** Share of a stroke's points that must lie inside the lasso (decision I6). */
export const LASSO_RATIO = 0.5;

export interface LassoOptions {
  /** Elements for attached strokes (e.g. `BoardData.freeform`). */
  elements?: ElementLookup;
  /** Also select free-form elements (Shift / toggle); never stage objects. */
  includeShapes?: readonly (RotatedRect & { id: string })[];
}

export interface LassoSelection {
  strokeIds: string[];
  elementIds: string[];
}

/**
 * Strokes with ≥ 50 % of their points inside the lasso polygon; with `includeShapes` also the
 * free-form elements whose centre lies inside.
 */
export function strokesInLasso(
  strokes: readonly InkStroke[],
  lasso: readonly Pt[],
  opts: LassoOptions = {},
): LassoSelection {
  if (lasso.length < 3) return { strokeIds: [], elementIds: [] };
  const strokeIds = strokes
    .filter((s) => {
      const pts = strokeBoardPoints(s, opts.elements);
      if (pts === null || pts.length === 0) return false;
      return pts.filter((p) => pointInPolygon(p, lasso)).length / pts.length >= LASSO_RATIO;
    })
    .map((s) => s.id);
  const elementIds = (opts.includeShapes ?? [])
    .filter((el) => pointInPolygon(rectCenter(el), lasso))
    .map((el) => el.id);
  return { strokeIds, elementIds };
}

export interface TransformOptions {
  elements?: ElementLookup;
  /** Scale the stroke width with the transform (lasso scaling); default true. */
  scaleWidth?: boolean;
}

/**
 * Applies a board-space transform (lasso move / scale / rotate) to a stroke. Attached strokes
 * stay attached: the transformed points are stored in local coordinates again.
 */
export function transformStroke(
  stroke: InkStroke,
  m: Affine,
  opts: TransformOptions = {},
): InkStroke {
  const elements = opts.elements ?? {};
  const points = strokeBoardPoints(stroke, elements);
  if (points === null) return stroke;
  const moved = points.map((p) => ({ ...p, ...applyAffine(m, p) }));
  const next = withBoardPoints(stroke, moved, elements);
  return opts.scaleWidth === false ? next : { ...next, width: stroke.width * scaleFactor(m) };
}

/** Copies of strokes with new ids, offset by (dx, dy) – duplicate / paste. Copies are detached. */
export function duplicateStrokes(
  strokes: readonly InkStroke[],
  dx: number,
  dy: number,
  opts: { elements?: ElementLookup; newId?: () => string; now?: number } = {},
): InkStroke[] {
  const makeId = opts.newId ?? (() => newId());
  const elements = opts.elements ?? {};
  return strokes.flatMap((s) => {
    const pts = strokeBoardPoints(s, elements);
    if (pts === null) return [];
    const moved = pts.map((p) => ({ ...p, x: p.x + dx, y: p.y + dy }));
    const { attachedTo: _a, attachedSize: _s, ...rest } = s;
    const copy = withBoardPoints(rest, moved);
    return [{ ...copy, id: makeId(), createdAt: opts.now ?? s.createdAt }];
  });
}

/** Board-space bounding box of strokes (selection frame for lasso handles). */
export function strokesBounds(
  strokes: readonly InkStroke[],
  elements: ElementLookup = {},
): { x: number; y: number; w: number; h: number } | null {
  const b = boundsOf(strokes.flatMap((s) => strokeBoardPoints(s, elements) ?? []));
  return b && { x: b.minX, y: b.minY, w: b.maxX - b.minX, h: b.maxY - b.minY };
}
